import { useState, useEffect, useCallback } from 'react';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';
import { Package, CheckCircle2, RotateCw, Loader2, AlertTriangle, ExternalLink } from 'lucide-react';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { Can, useCan } from '../../lib/rbac';

interface OrphanRow {
  id: number;
  seller_id: number;
  ml_order_id: number;
  asin: string | null;
  quantity: number;
  unit_cost: number | null;
  total_cost: number | null;
  image_url: string | null;
  product_title: string | null;
  amazon_status: string | null;
  amazon_order_id: string | null;
  tracking_number: string | null;
  eta: string | null;
  ml_order_status: string | null;
  buyer_nickname: string | null;
  orphan_reason: string | null;
  orphan_at: string | null;
  notes: string | null;
}

const AMZ_STATUS_MAP: Record<string, 'ok' | 'warning' | 'error' | 'info' | 'unknown'> = {
  ordered: 'info',
  shipped: 'warning',
  out_for_delivery: 'warning',
  delivered: 'ok',
  delivered_ml: 'ok',
  cancelled: 'error',
};

const fmtMoney = (v: number | null | undefined) =>
  v == null || Number.isNaN(Number(v)) ? '—' : `$${Number(v).toFixed(2)}`;

const fmtDate = (v: string | null | undefined) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

export default function OrphansTab() {
  const { sellerId } = useUIStore();
  const can = useCan();
  const canMoney = can('global.view_money');
  const [rows, setRows] = useState<OrphanRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState<number | null>(null);
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp: any = await api.get(`/order-holds/orphans?seller_id=${sellerId}`);
      setRows(resp?.data ?? []);
    } catch (err: any) {
      setToast({ kind: 'err', msg: err?.message ?? 'Error cargando huérfanas' });
    } finally {
      setLoading(false);
    }
  }, [sellerId]);

  useEffect(() => { load(); }, [load]);

  const showToast = (t: typeof toast) => {
    setToast(t);
    if (t) setTimeout(() => setToast(null), 4000);
  };

  const admit = async (id: number) => {
    if (!confirm('¿Admitir al inventario? Suma la unidad al stock disponible.')) return;
    setWorking(id);
    try {
      await api.post(`/order-holds/${id}/admit-to-inventory?seller_id=${sellerId}`, {});
      showToast({ kind: 'ok', msg: 'Admitida al inventario' });
      load();
    } catch (err: any) {
      showToast({ kind: 'err', msg: err?.message ?? 'Error' });
    } finally {
      setWorking(null);
    }
  };

  const retryCancel = async (id: number) => {
    setWorking(id);
    try {
      const resp: any = await api.post(`/order-holds/${id}/cancel-again?seller_id=${sellerId}`, {});
      if (resp?.data?.cancelled) {
        showToast({ kind: 'ok', msg: 'Amazon aceptó la cancelación' });
      } else {
        showToast({ kind: 'err', msg: `Amazon rechazó: ${resp?.data?.error ?? 'desconocido'}` });
      }
      load();
    } catch (err: any) {
      showToast({ kind: 'err', msg: err?.message ?? 'Error' });
    } finally {
      setWorking(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12 text-[var(--color-text-muted)]">
        <Loader2 size={20} className="animate-spin mr-2" /> Cargando huérfanas...
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="text-center py-12 text-[var(--color-text-muted)]">
        <CheckCircle2 size={32} className="mx-auto mb-3 text-[var(--color-success)]" />
        <div>No hay compras huérfanas. Todo en orden.</div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-3 bg-[var(--color-warning-bg,rgba(234,179,8,0.1))] border border-[var(--color-warning)] rounded-lg p-3 text-sm">
        <AlertTriangle size={18} className="text-[var(--color-warning)] shrink-0 mt-0.5" />
        <div className="text-[var(--color-text)]">
          <strong>{rows.length} compra{rows.length !== 1 ? 's' : ''} huérfana{rows.length !== 1 ? 's' : ''}</strong> — ordenes ML canceladas cuya compra Amazon procedió.
          Decidí qué hacer cuando llegue el paquete al depósito:
          <span className="font-semibold"> Admitir al inventario</span> (queda como stock tuyo) o
          <span className="font-semibold"> Reintentar cancel Amazon</span> (best-effort, hoy el API falla casi siempre con 403).
        </div>
      </div>

      {toast && (
        <div className={`rounded-lg p-3 text-sm ${toast.kind === 'ok' ? 'bg-[var(--color-success-bg,rgba(34,197,94,0.1))] text-[var(--color-success)]' : 'bg-[var(--color-error-bg,rgba(239,68,68,0.1))] text-[var(--color-error)]'}`}>
          {toast.msg}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
            <tr>
              <th className="p-2">Producto</th>
              <th className="p-2">ASIN</th>
              <th className="p-2">Cant.</th>
              <th className="p-2">Costo</th>
              <th className="p-2">Amazon</th>
              <th className="p-2">Tracking</th>
              <th className="p-2">ETA</th>
              <th className="p-2">Orden ML</th>
              <th className="p-2">Huérfana desde</th>
              <th className="p-2 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-[var(--color-border-light)] hover:bg-[var(--color-surface-hover)]">
                <td className="p-2">
                  <div className="flex gap-2 items-center max-w-[240px]">
                    <div className="w-8 h-8 rounded overflow-hidden bg-[var(--color-border-light)] shrink-0">
                      {r.image_url ? <img src={r.image_url} alt="" className="w-full h-full object-contain" /> :
                        <Package size={14} className="w-full h-full p-2 text-[var(--color-text-muted)]" />}
                    </div>
                    <span className="text-xs truncate">{r.product_title || r.asin || 'Sin título'}</span>
                  </div>
                </td>
                <td className="p-2 font-mono text-xs">{r.asin || '—'}</td>
                <td className="p-2 font-mono text-xs">{r.quantity}</td>
                <td className="p-2 font-mono text-xs">{canMoney ? fmtMoney(r.total_cost ?? r.unit_cost) : '—'}</td>
                <td className="p-2">
                  {r.amazon_status && <StatusBadge status={AMZ_STATUS_MAP[r.amazon_status] || 'unknown'} label={r.amazon_status} size="sm" />}
                </td>
                <td className="p-2 font-mono text-xs truncate max-w-[120px]">{r.tracking_number || '—'}</td>
                <td className="p-2 text-xs">{fmtDate(r.eta)}</td>
                <td className="p-2 text-xs">
                  <span className="font-mono text-[var(--color-primary)]">#{r.ml_order_id}</span>
                  {r.buyer_nickname && <div className="text-[10px] text-[var(--color-text-muted)]">{r.buyer_nickname}</div>}
                </td>
                <td className="p-2 text-xs">{fmtDate(r.orphan_at)}</td>
                <td className="p-2">
                  <div className="flex gap-1 justify-end flex-wrap">
                    <Can cap="purchases.operate_pos">
                      <button
                        onClick={() => admit(r.id)}
                        disabled={working === r.id}
                        className="px-2 py-1 rounded text-xs bg-[var(--color-success)] text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-1"
                        title="Queda como stock tuyo">
                        {working === r.id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                        Admitir
                      </button>
                      <button
                        onClick={() => retryCancel(r.id)}
                        disabled={working === r.id}
                        className="px-2 py-1 rounded text-xs border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] disabled:opacity-50 flex items-center gap-1"
                        title="Reintenta cancelación Amazon">
                        <RotateCw size={12} />
                        Reintentar
                      </button>
                    </Can>
                    {r.amazon_order_id && (
                      <a
                        href={`https://www.amazon.com/gp/your-account/order-details?orderID=${r.amazon_order_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="px-2 py-1 rounded text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] flex items-center gap-1"
                        title="Ver en Amazon">
                        <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
